import type { ComponentPropsWithoutRef } from "react";
import { t } from "ttag";

import { useScheduleContext } from "@/features/consultations/contexts";
import { useProvidersQuery } from "@/features/consultations/hooks";
import type { Provider } from "@/features/consultations/models";
import { PROVIDER_PREFERENCE } from "@/shared/models/legacy-constants";
import { Card, MagnifierIcon, Select, UserIcon } from "@/shared/ui";

interface ScheduleProviderPreferenceCardProps
  extends ComponentPropsWithoutRef<typeof Card.Root> {
  title: string;
}

export const ScheduleProviderPreferenceCard = ({
  title,
  ...props
}: ScheduleProviderPreferenceCardProps) => {
  const {
    providerPreference,
    provider,
    actions: { setProviderPreference, setProvider, setScheduleDate },
  } = useScheduleContext();

  const { data, isLoading } = useProvidersQuery();

  const providerOptions = data?.map((provider) => ({
    value: provider.id,
    label: provider.fullName,
  }));

  const handleSelectPreference = (preference: typeof providerPreference) => {
    setProviderPreference(preference);
    setScheduleDate((prev) => ({ ...prev, timeSlot: undefined }));

    if (preference === PROVIDER_PREFERENCE.EARLIEST) {
      setProvider();
    } else {
      setScheduleDate((prev) => ({ ...prev, appointmentDate: undefined }));
    }
  };

  const handleSelectedProvider = (id?: Provider["id"]) => {
    const selectedProvider = data?.find((provider) => provider.id === id);
    setProvider(selectedProvider);
    setScheduleDate((prev) => ({
      ...prev,
      appointmentDate: undefined,
      timeSlot: undefined,
    }));
  };

  return (
    <Card.Root {...props}>
      <Card.Title>{title}</Card.Title>

      <Card.Content className="flex flex-col items-center gap-x-3 gap-y-6 lg:flex-row">
        <Select
          leftIcon={<UserIcon />}
          id="provider-preference-select"
          value={providerPreference}
          onChange={handleSelectPreference}
          options={[
            {
              value: PROVIDER_PREFERENCE.EARLIEST,
              label: t`Earliest available`,
            },
            {
              value: PROVIDER_PREFERENCE.CUSTOM,
              label: t`Choose a provider`,
            },
          ]}
        />

        {providerPreference === PROVIDER_PREFERENCE.CUSTOM && (
          <Select
            value={provider?.id ?? ""}
            onChange={handleSelectedProvider}
            options={providerOptions ?? []}
            placeholder={
              isLoading ? t`Loading providers...` : t`Select a provider`
            }
            id="provider-select"
            leftIcon={<MagnifierIcon />}
            disabled={isLoading}
          />
        )}
      </Card.Content>
    </Card.Root>
  );
};
